import React, { useState, useRef } from 'react';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Format data results for display in chat
const formatDataResponse = (data, fields, rowCount, explanation) => {
  if (!data || data.length === 0) {
    return explanation || 'No results found.';
  }

  // For single value results (like COUNT)
  if (data.length === 1 && fields.length === 1) {
    const value = data[0][fields[0]];
    return `${explanation || 'Result'}: **${value}**`;
  }

  // For small datasets, show a compact table
  if (data.length <= 5) {
    let result = explanation ? `${explanation}\n\n` : '';
    result += `Found ${rowCount} result${rowCount !== 1 ? 's' : ''}:\n`;
    data.forEach((row, i) => {
      const values = fields.slice(0, 3).map(f => `${f}: ${row[f] ?? 'N/A'}`).join(', ');
      result += `• ${values}${fields.length > 3 ? '...' : ''}\n`;
    });
    return result.trim();
  }

  // For larger datasets
  return `${explanation || 'Query completed'}. Found **${rowCount} results**. Use the Data Query panel for full table view and export.`;
};

const Chatbot = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: 'assistant', content: 'Hi! I can answer questions about your database.\n\nTry asking:\n• "How many counties do we have?"\n• "Count enriched owners"\n• "Show email stats for county 5"' }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const messagesEndRef = useRef(null);

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!input.trim()) return;
    setError(null);
    const userMsg = { role: 'user', content: input };
    setMessages(msgs => [...msgs, userMsg]);
    const currentInput = input;
    setInput('');
    setLoading(true);

    try {
      // Use the data-query endpoint for database questions
      const res = await fetch(`${API_BASE_URL}/data-query`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ 
          prompt: currentInput,
          pageSize: 10 // Limit results for chat view
        })
      });

      const data = await res.json();

      if (!res.ok || !data.success) {
        const errorMsg = data.message || data.error || 'Error processing your question.';
        setError(errorMsg);
        setMessages(msgs => [...msgs, { role: 'assistant', content: errorMsg }]);
      } else {
        let reply = '';

        switch (data.type) {
          case 'data':
            reply = formatDataResponse(data.data, data.fields, data.rowCount, data.explanation);
            break;
          case 'answer':
            reply = data.message || data.explanation || 'Done.';
            break;
          case 'clarification':
            reply = data.message || 'I need more information.';
            if (data.questions && data.questions.length > 0) {
              reply += '\n\n' + data.questions.map(q => `• ${q}`).join('\n');
            }
            break;
          default:
            reply = data.message || 'I processed your request.';
        }

        setMessages(msgs => [...msgs, { role: 'assistant', content: reply }]);
      }
    } catch (err) {
      console.error('Chatbot error:', err);
      setError('Network error. Please try again.');
      setMessages(msgs => [...msgs, { role: 'assistant', content: 'Network error. Please check your connection.' }]);
    }
    setLoading(false);
  };

  // Clear chat
  const clearChat = () => {
    setMessages([
      { role: 'assistant', content: 'Chat cleared. How can I help you?' }
    ]);
    setError(null);
  };

  // Scroll to bottom on new message
  React.useEffect(() => {
    if (open && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, open]);

  // Render message with basic markdown-like formatting
  const renderMessage = (content) => {
    return content.split('\n').map((line, i) => {
      // Bold text
      const formattedLine = line.split(/(\*\*.*?\*\*)/).map((part, j) => {
        if (part.startsWith('**') && part.endsWith('**')) {
          return <strong key={j}>{part.slice(2, -2)}</strong>;
        }
        return part;
      });

      return (
        <span key={i}>
          {i > 0 && <br />}
          {formattedLine}
        </span>
      );
    });
  };

  return (
    <>
      {/* Floating Button */}
      <button
        className={`fixed bottom-6 right-6 z-50 bg-blue-600 text-white rounded-full shadow-lg w-14 h-14 flex items-center justify-center text-2xl hover:bg-blue-700 focus:outline-none transition-transform ${open ? 'scale-90' : 'scale-100'}`}
        onClick={() => setOpen(o => !o)}
        aria-label="Open Chatbot"
        style={{ boxShadow: open ? '0 0 0 4px #3b82f6' : undefined }}
      >
        💬
      </button>
      {/* Chat Window */}
      {open && (
        <div className="fixed bottom-24 right-6 z-50 w-96 bg-white rounded-xl shadow-2xl flex flex-col overflow-hidden border border-gray-200 animate-fade-in">
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-4 py-3 font-semibold flex items-center justify-between">
            <span className="flex items-center">
              <span className="mr-2">🤖</span>
              Data Assistant
            </span>
            <div className="flex items-center space-x-2">
              <button 
                onClick={clearChat} 
                className="text-white/70 hover:text-white text-xs px-2 py-1 rounded hover:bg-white/10"
                title="Clear chat"
              >
                Clear
              </button>
              <button onClick={() => setOpen(false)} className="text-white text-lg font-bold hover:text-blue-200">×</button>
            </div>
          </div>
          <div className="flex-1 p-4 space-y-3 overflow-y-auto max-h-96" style={{ minHeight: 200 }}>
            {messages.map((msg, i) => (
              <div key={i} className={`text-sm mb-1 transition-all ${msg.role === 'user' ? 'text-right' : 'text-left'}`}>
                <div className={`inline-block px-3 py-2 rounded-lg shadow-sm max-w-[90%] text-left ${
                  msg.role === 'user' 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-gray-100 text-gray-800'
                } animate-fade-in`}>
                  {renderMessage(msg.content)}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex items-center space-x-2 text-gray-400 animate-pulse">
                <span className="inline-block w-3 h-3 rounded-full bg-blue-400 animate-bounce" />
                <span className="inline-block w-3 h-3 rounded-full bg-blue-300 animate-bounce delay-75" />
                <span className="inline-block w-3 h-3 rounded-full bg-blue-200 animate-bounce delay-150" />
                <span>Querying...</span>
              </div>
            )}
            {error && (
              <div className="text-xs text-red-500 bg-red-50 px-3 py-2 rounded-lg">{error}</div>
            )}
            <div ref={messagesEndRef} />
          </div>
          <form onSubmit={sendMessage} className="flex border-t border-gray-200 bg-gray-50">
            <input
              type="text"
              className="flex-1 px-3 py-3 outline-none bg-transparent text-sm"
              placeholder="Ask about your data..."
              value={input}
              onChange={e => setInput(e.target.value)}
              disabled={loading}
              autoFocus
            />
            <button 
              type="submit" 
              className="px-4 text-blue-600 font-bold disabled:text-gray-400" 
              disabled={loading || !input.trim()}
            >
              Send
            </button>
          </form>
        </div>
      )}
      <style>{`
        .animate-fade-in { animation: fadeIn 0.3s; }
        @keyframes fadeIn { from { opacity: 0; transform: translateY(20px);} to { opacity: 1; transform: none; } }
        .animate-bounce { animation: bounce 1s infinite; }
        @keyframes bounce { 0%, 100% { transform: translateY(0);} 50% { transform: translateY(-8px);} }
        .delay-75 { animation-delay: 0.075s; }
        .delay-150 { animation-delay: 0.15s; }
      `}</style>
    </>
  );
};

export default Chatbot; 